import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fileUrl, api } from "../lib/api";
import { ThumbsUp, ThumbsDown, Send, Bookmark, Flag, Trash2, Play, BadgeCheck, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import ShareToFollowersModal from "./ShareToFollowersModal";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 2592000)}mo`;
}

export function VideoCard({ item, type = "debate" }) {
  const { user, isBookmarked, toggleBookmark } = useAuth();
  const navigate = useNavigate();
  const containerRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [inView, setInView] = useState(false);
  const [liked, setLiked] = useState(item.likes?.includes(user?.id));
  const [disliked, setDisliked] = useState(item.dislikes?.includes(user?.id));
  const [likesCount, setLikesCount] = useState(item.likes_count ?? item.likes?.length ?? 0);
  const [dislikesCount, setDislikesCount] = useState(item.dislikes_count ?? item.dislikes?.length ?? 0);
  const [shareOpen, setShareOpen] = useState(false);
  const saved = isBookmarked ? isBookmarked(type, item.id) : false;

  // Auto-play the video when its media area is at least 55% in view, pause when it leaves.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting && entry.intersectionRatio > 0.55);
      },
      { threshold: [0, 0.55, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  }, [inView]);

  const thumb = item.thumbnail_url ? fileUrl(item.thumbnail_url) : null;
  const videoSrc = item.video_url ? fileUrl(item.video_url) : null;

  const positionMeta =
    item.position === "support"
      ? { label: "SUPPORTS", cls: "bg-[hsl(var(--support))]" }
      : item.position === "oppose"
      ? { label: "OPPOSES", cls: "bg-[hsl(var(--oppose))]" }
      : null;

  const detailPath = type === "debate" ? `/debates/${item.id}` : `/thoughts`;

  // Like / Dislike behavior differs by type:
  // - Debate: navigate to detail with ?reply=<position> — no local count change
  // - Thought: toggle like/dislike via API
  const handleLike = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (type === "debate") {
      navigate(`/debates/${item.id}?reply=support`);
      return;
    }
    try {
      await api.post(`/thoughts/${item.id}/like`);
      if (liked) { setLiked(false); setLikesCount((c) => c - 1); }
      else {
        setLiked(true); setLikesCount((c) => c + 1);
        if (disliked) { setDisliked(false); setDislikesCount((c) => c - 1); }
      }
    } catch { toast.error("Login required"); }
  };

  const handleDislike = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (type === "debate") {
      navigate(`/debates/${item.id}?reply=oppose`);
      return;
    }
    try {
      await api.post(`/thoughts/${item.id}/dislike`);
      if (disliked) { setDisliked(false); setDislikesCount((c) => c - 1); }
      else {
        setDisliked(true); setDislikesCount((c) => c + 1);
        if (liked) { setLiked(false); setLikesCount((c) => c - 1); }
      }
    } catch { toast.error("Login required"); }
  };

  const share = (e) => {
    e.preventDefault(); e.stopPropagation();
    setShareOpen(true);
  };

  const togglePlay = (e) => {
    e.preventDefault(); e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().then(() => setPlaying(true)).catch(() => {}); } else { v.pause(); setPlaying(false); }
  };

  return (
    <article
      className="bg-card border border-border rounded-2xl overflow-hidden card-shadow"
      data-testid={`${type}-card-${item.id}`}
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3">
        <Link to={`/profile/${item.author?.username}`} className="flex items-center gap-3 flex-1 min-w-0 group">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(346_78%_55%)] p-[2px]">
            <div className="w-full h-full rounded-full bg-card flex items-center justify-center overflow-hidden">
              {item.author?.profile_photo ? (
                <img src={fileUrl(item.author.profile_photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-semibold text-sm text-[hsl(var(--primary))]">
                  {item.author?.username?.[0]?.toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1 group-hover:text-[hsl(var(--primary))] transition-colors">
              @{item.author?.username}
              {item.author?.verification_status === "verified" && (
                <BadgeCheck className="w-4 h-4 fill-[hsl(var(--primary))] text-white" />
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {timeAgo(item.created_at)} · {type === "debate" ? "Debate" : "Thought"}
            </div>
          </div>
        </Link>
        {positionMeta && (
          <div className={`text-white ${positionMeta.cls} px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wider`}>
            {positionMeta.label}
          </div>
        )}
      </header>

      {/* Media */}
      <Link to={detailPath} className="block relative aspect-square bg-black group" onClick={togglePlay} ref={containerRef}>
        {videoSrc ? (
          <video
            ref={videoRef}
            src={videoSrc}
            poster={thumb || undefined}
            muted={muted}
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        ) : thumb ? (
          <img src={thumb} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary-soft))] to-[hsl(var(--secondary))]">
            <Play className="w-14 h-14 text-[hsl(var(--primary))]" strokeWidth={1.5} />
          </div>
        )}
        {!playing && videoSrc && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
              <Play className="w-7 h-7 text-[hsl(var(--primary))] fill-current ml-1" />
            </div>
          </div>
        )}
        {videoSrc && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMuted((m) => !m); }}
            className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
            data-testid={`mute-${item.id}`}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
      </Link>

      {/* Actions — Like + Dislike + Share + Bookmark. NO comment. */}
      <div className="px-4 pt-3 flex items-center gap-1">
        <button
          onClick={handleLike}
          className="p-2 hover:bg-muted rounded-full transition-colors group"
          data-testid={`like-${item.id}`}
          aria-label={type === "debate" ? "Support this debate" : "Like"}
        >
          <ThumbsUp className={`w-6 h-6 transition-all ${liked ? "fill-[hsl(var(--support))] text-[hsl(var(--support))]" : "text-foreground group-hover:text-[hsl(var(--support))]"}`} strokeWidth={1.75} />
        </button>
        <button
          onClick={handleDislike}
          className="p-2 hover:bg-muted rounded-full transition-colors group"
          data-testid={`dislike-${item.id}`}
          aria-label={type === "debate" ? "Oppose this debate" : "Dislike"}
        >
          <ThumbsDown className={`w-6 h-6 transition-all ${disliked ? "fill-[hsl(var(--oppose))] text-[hsl(var(--oppose))]" : "text-foreground group-hover:text-[hsl(var(--oppose))]"}`} strokeWidth={1.75} />
        </button>
        <button onClick={share} className="p-2 hover:bg-muted rounded-full transition-colors" data-testid={`share-${item.id}`}>
          <Send className="w-6 h-6" strokeWidth={1.75} />
        </button>
        <div className="flex-1" />
        {user?.id === item.author?.id ? (
          <button
            onClick={async (e) => {
              e.preventDefault(); e.stopPropagation();
              if (!window.confirm("Delete this post? (Only allowed on the same day.)")) return;
              try {
                await api.delete(`/${type === "debate" ? "debates" : "thoughts"}/${item.id}`);
                toast.success("Deleted");
                window.location.reload();
              } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
            }}
            className="p-2 hover:bg-muted rounded-full transition-colors group"
            data-testid={`delete-${item.id}`}
            aria-label="Delete post"
            title="Delete post"
          >
            <Trash2 className="w-5 h-5 text-foreground group-hover:text-[hsl(var(--oppose))] transition-colors" strokeWidth={1.75} />
          </button>
        ) : (
          <button
            onClick={async (e) => {
              e.preventDefault(); e.stopPropagation();
              if (!window.confirm("Report this post as spam? Our admins will review it.")) return;
              try {
                await api.post(`/reports/${type}/${item.id}`, { reason: "spam" });
                toast.success("Reported. Admin will review.");
              } catch { toast.error("Failed to report"); }
            }}
            className="p-2 hover:bg-muted rounded-full transition-colors group"
            data-testid={`report-${item.id}`}
            aria-label="Report spam"
            title="Report spam"
          >
            <Flag className="w-5 h-5 text-foreground group-hover:text-[hsl(var(--oppose))] transition-colors" strokeWidth={1.75} />
          </button>
        )}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleBookmark?.(type, item.id); }}
          className="p-2 hover:bg-muted rounded-full transition-colors group"
          data-testid={`bookmark-${item.id}`}
          aria-label={saved ? "Remove bookmark" : "Save"}
        >
          <Bookmark
            className={`w-6 h-6 transition-all ${saved ? "fill-foreground text-foreground" : "text-foreground group-hover:text-[hsl(var(--primary))]"}`}
            strokeWidth={1.75}
          />
        </button>
      </div>

      {/* Stats */}
      <div className="px-4 pt-1 pb-4">
        {type === "thought" ? (
          <div className="text-sm font-semibold mb-1">
            {likesCount.toLocaleString()} likes · {dislikesCount.toLocaleString()} dislikes
          </div>
        ) : (
          <div className="flex items-center gap-4 text-sm mb-1" data-testid={`debate-vote-summary-${item.id}`}>
            <span className="flex items-center gap-1.5 font-semibold text-[hsl(var(--support))]">
              <span className="w-2 h-2 rounded-full bg-[hsl(var(--support))]" />
              {item.support_count || 0} support
            </span>
            <span className="flex items-center gap-1.5 font-semibold text-[hsl(var(--oppose))]">
              <span className="w-2 h-2 rounded-full bg-[hsl(var(--oppose))]" />
              {item.oppose_count || 0} oppose
            </span>
          </div>
        )}
        <div className="font-serif text-lg leading-snug" data-testid={`title-${item.id}`}>
          {item.title}
        </div>
        {item.description && (
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{item.description}</p>
        )}
        <Link
          to={detailPath}
          className="block text-xs text-muted-foreground mt-2 hover:text-foreground"
          data-testid={`view-${type}-${item.id}`}
        >
          {type === "debate" ? "Enter the debate →" : "View →"}
        </Link>
      </div>

      <ShareToFollowersModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareRef={{ type, id: item.id, title: item.title, thumbnail_url: item.thumbnail_url }}
        shareUrl={`${window.location.origin}${detailPath}`}
      />
    </article>
  );
}
